import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxPopupModule } from 'devextreme-angular/ui/popup';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import {
  MappingGridRow,
  MappingPicklistOption,
  FieldOption
} from '../../models/link-generic-mapper.models';

export interface PicklistValueMapItem {
  sourceValue: string;
  sourceLabel: string;
  targetValue?: string | null;
}

export interface PicklistMappingConfiguration {
  sourceFieldPath: string;
  items: PicklistValueMapItem[];
}

type PicklistMappingViewFilter = 'all' | 'mapped' | 'unmapped';

@Component({
  selector: 'app-picklist-value-mapping-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DxPopupModule,
    DxButtonModule,
    DxDataGridModule,
    DxSelectBoxModule
  ],
  templateUrl: './picklist-value-mapping-popup.component.html',
  styleUrl: './picklist-value-mapping-popup.component.scss'
})
export class PicklistValueMappingPopupComponent {
	readonly autoMatchButtonElementAttr = { 'data-cy': 'mapper-picklist-auto-match-button' };
	readonly applyMappingButtonElementAttr = { 'data-cy': 'mapper-picklist-apply-button' };

  @Input({ required: true }) visible = false;
  @Input({ required: true }) row: MappingGridRow | null = null;
  @Input({ required: true }) sourceField: FieldOption | null = null;
  @Input() sourceSystemName = 'Source';
  @Input() targetSystemName = 'Target';
  @Input() sourceOptions: MappingPicklistOption[] = [];
  @Input() targetOptions: MappingPicklistOption[] = [];
  @Input() mapping: PicklistMappingConfiguration | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() apply = new EventEmitter<PicklistMappingConfiguration>();
  @Output() cancel = new EventEmitter<void>();

  localItems: PicklistValueMapItem[] = [];
	filteredItems: PicklistValueMapItem[] = [];
	viewFilter: PicklistMappingViewFilter = 'all';

	get mappedItemCount(): number {
		return this.localItems.filter(item => this.isMapped(item)).length;
	}

	get unmappedItemCount(): number {
		return this.localItems.length - this.mappedItemCount;
	}

  ngOnChanges(): void {
    this.rebuildLocalItems();
  }

  close(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  save(): void {
    if (!this.sourceField) {
      return;
    }

    this.apply.emit({
      sourceFieldPath: this.sourceField.path,
      items: this.localItems.map(x => ({ ...x }))
    });

    this.visibleChange.emit(false);
  }

  autoMatch(): void {
    for (const item of this.localItems) {
      const sourceTokens = this.getTokens(item.sourceValue, item.sourceLabel);

      const match = this.targetOptions.find(target => {
        const targetTokens = this.getTokens(
          target.value,
          target.label,
          target.canonicalKey ?? undefined,
          ...(target.aliases ?? [])
        );

        return [...sourceTokens].some(token => targetTokens.has(token));
      });

      item.targetValue = match?.value ?? null;
    }

		this.applyViewFilter();
  }

	autoMatchAndSaveFromEvent(event: Event): void {
		event.stopPropagation();
		this.autoMatch();
		this.save();
	}

  clearAll(): void {
    for (const item of this.localItems) {
      item.targetValue = null;
    }

		this.applyViewFilter();
  }

	setViewFilter(filter: PicklistMappingViewFilter): void {
		this.viewFilter = filter;
		this.applyViewFilter();
	}

	setTargetValue(item: PicklistValueMapItem, targetValue: string | null): void {
		item.targetValue = targetValue;
		this.applyViewFilter();
	}

  private rebuildLocalItems(): void {
    const existing = new Map(
      (this.mapping?.items ?? []).map(x => [x.sourceValue, x.targetValue ?? null])
    );

    this.localItems = (this.sourceOptions ?? []).map(source => ({
      sourceValue: source.value,
      sourceLabel: source.label,
      targetValue: existing.get(source.value) ?? null
    }));

		this.applyViewFilter();
  }

	private applyViewFilter(): void {
		this.filteredItems = this.localItems.filter(item => {
			if (this.viewFilter === 'mapped') {
				return this.isMapped(item);
			}

			if (this.viewFilter === 'unmapped') {
				return !this.isMapped(item);
			}

			return true;
		});
	}

	private isMapped(item: PicklistValueMapItem): boolean {
		return item.targetValue !== null
			&& item.targetValue !== undefined
			&& item.targetValue !== '';
	}

  private getTokens(...values: Array<string | null | undefined>): Set<string> {
    const set = new Set<string>();

    for (const value of values) {
      if (!value?.trim()) {
        continue;
      }

      set.add(value.trim());
      set.add(value.trim().toLowerCase());
      set.add(value.trim().toLowerCase().replace(/[\s\-_]+/g, ' '));
    }

    return set;
  }
}
